// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Event } from '../Event/Event';
import { EventSource, EventType } from '../Event/EventConstants';
import { DateTimeUtils } from '@shared/utils/Time/DateTimeUtils';
import { Resources } from '@shared/localizations/Resources';
import { CustomEvent } from '@shared/utils/CustomEvents';
import { ReportMessageImportanceLevel } from '@shared/utils/Enums/Constants';
import { BusinessRejectMessagesKey, BusinessRejectTextCodeDict } from '../../BusinessRejectUtils/BusinessRejectUtils';
import { UserIndexedDB, UserIndexedDBKey, UserIndexedDBStore } from '../../DataBase/UserIndexedDB';
import { UserManager } from '@shared/utils/Managers/UserManager';
import { ClientType } from '@shared/utils/ClientType';
class _EventCache {
    constructor() {
        this.indexedDB = UserIndexedDB;
        this.events = [];
        this.OnAddEvent = new CustomEvent();
        this.OnClearEvents = new CustomEvent();
    }
    get userId() {
        return UserManager.getUserKey(ClientType.getClientType());
    }
    get beforeLoginKey() {
        return UserManager.getBeforeLoginKey(ClientType.getClientType());
    }
    loaded() {
        this.deleteFromDB();
    }
    Clear() {
        this.events = [];
        this.OnClearEvents.Raise();
    }
    GetEvents() {
        return this.events.slice();
    }
    Add(ev, isAddToRT = true) {
        // +++yura - если операция торговая - добавляем в лог в соответствии с настройкой
        if (ev.EventType === EventType.Trading && !_EventCache.AddTradingToLog) {
            return;
        }
        if (isAddToRT) {
            this.addEventToRealTime(ev);
        }
        this.addEventToDB(ev);
    }
    AddException(ex) {
        const timestamp = new Date();
        const ev = new Event({
            DateTime: timestamp,
            Description: ex.message,
            EventType: EventType.Exception
        });
        const isAddToRT = !ClientType.IsWebMobile();
        this.Add(ev, isAddToRT);
    }
    isDailyLimitExist(ev) {
        if (ev.BusinessRejectCode !== BusinessRejectTextCodeDict[BusinessRejectMessagesKey.DailyLimit]) {
            return false;
        }
        const events = this.GetEvents();
        for (const event of events) {
            if (event.BusinessRejectCode === ev.BusinessRejectCode) {
                return true;
            }
        }
        return false;
    }
    FromReportMessage(msg) {
        var _a;
        let str = '';
        let acc = '';
        // let productTypeString = '';
        const data = msg.Data;
        const len = data.length;
        for (let i = 0; i < len; i++) {
            const row = data[i];
            const cell0 = row[0];
            const cell1 = Resources.getResource(row[1]);
            if (cell0 === 'Account') {
                acc = cell1;
            }
            // if (cell0 === ORDER_PARAMETER_PRODUCT_TYPE) {
            //     productTypeString = cell1;
            // }
            if (!Resources.isHidden('reports.' + cell0)) {
                str += cell0 + ':' + cell1 + ';';
            }
        }
        let source = msg.ImportanceLevel === ReportMessageImportanceLevel.Critical ? EventSource.MarginCall : EventSource.In;
        if (msg.reject && msg.BusinessRejectCode !== 392) {
            source = EventSource.Refused;
        }
        return new Event({
            DateTime: DateTimeUtils.DateTimeUtcNow(),
            Action: Resources.getResource(msg.Name),
            Description: str,
            // TODO. UGLY. Refactor. Remove when BusinessRejectMessage is treated correctly.
            EventType: EventType.Trading,
            EventSource: source,
            Account: acc !== null && acc !== void 0 ? acc : (_a = msg.account) === null || _a === void 0 ? void 0 : _a.FullAccString,
            ProductType: msg.productType,
            // TODO. Ok? Pass instrument id maybe?
            Instrument: msg.Instrument,
            Roundtrip: msg.Roundtrip,
            BusinessRejectCode: msg.BusinessRejectCode
        });
    }
    log(action, description = undefined, eventType = undefined, eventSource = undefined) {
        if (!action)
            return;
        this.Add(new Event({
            DateTime: DateTimeUtils.DateTimeUtcNow(),
            Action: action,
            Description: description,
            EventType: eventType,
            EventSource: eventSource
        }));
    }
    getEventsFromDB() {
        return __awaiter(this, void 0, void 0, function* () {
            // MIGRATION BLOCK BEGIN - must be removed >= 3.118
            const oldUserId = UserManager.getUserKey();
            const oldBeforeLoginKey = UserManager.getBeforeLoginKey();
            const oldUserEventObjects = yield this.indexedDB.getAllByIndex(UserIndexedDBStore.EVENT_LOG, UserIndexedDBKey.USER_ID, oldUserId);
            const oldCommonEventObjects = yield this.indexedDB.getAllByIndex(UserIndexedDBStore.EVENT_LOG, UserIndexedDBKey.USER_ID, oldBeforeLoginKey);
            // BLOCK END
            const userEventObjects = yield this.indexedDB.getAllByIndex(UserIndexedDBStore.EVENT_LOG, UserIndexedDBKey.USER_ID, this.userId);
            const commonEventObjects = yield this.indexedDB.getAllByIndex(UserIndexedDBStore.EVENT_LOG, UserIndexedDBKey.USER_ID, this.beforeLoginKey);
            const eventObjects = [
                ...oldUserEventObjects,
                ...oldCommonEventObjects,
                ...userEventObjects,
                ...commonEventObjects
            ];
            const events = [];
            eventObjects.forEach((eventObject) => {
                const event = new Event({
                    DateTime: eventObject.DateTime,
                    Action: eventObject.Action,
                    Description: eventObject.DescriptionItems,
                    EventType: eventObject.EventType,
                    EventSource: eventObject.Source
                });
                event.RouteID = eventObject.RouteID;
                event.InstrumentTradableID = eventObject.InstrumentTradableID;
                events.push(event);
            });
            events.sort((a, b) => a.timestamp - b.timestamp);
            return events;
        });
    }
    addEventToRealTime(event) {
        this.events.push(event);
        this.OnAddEvent.Raise(event);
    }
    addEventToDB(event) {
        var _a;
        event.userId = (_a = this.userId) !== null && _a !== void 0 ? _a : this.beforeLoginKey;
        void this.indexedDB.addOrUpdate(UserIndexedDBStore.EVENT_LOG, event);
    }
    deleteFromDB() {
        // delete all records older than 7 days in UTC.
        const upperBoundUTC = Date.now() - 7 * 24 * 60 * 60 * 1000;
        const lowerBoundUTC = 0;
        void this.indexedDB.deleteInRangeByIndex(UserIndexedDBStore.EVENT_LOG, UserIndexedDBKey.TIMESTAMP, lowerBoundUTC, upperBoundUTC);
    }
}
_EventCache.AddTradingToLog = true;
export const EventCache = new _EventCache();
